import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ChevronDownIconLegacy from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIconLegacy from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

const CONTENT_OFFSET = token('space.100');
const WRAPPER_TRANSFORM = `translate(calc(-100% - ${CONTENT_OFFSET}), -50%)`;

export const ExpandIcon = ({
	expanded,
	onClick,
	rowIndex,
	totalGroupItems,
}: {
	expanded: boolean;
	rowIndex?: number;
	totalGroupItems: number;
	onClick: () => void;
}) => {
	const { formatMessage } = useIntl();
	const Icon = expanded ? (
		<ChevronDownIcon
			label={formatMessage(messages.collapseLabel)}
			color={token('color.text.subtle')}
			LEGACY_primaryColor={token('color.text.subtle')}
			LEGACY_fallbackIcon={ChevronDownIconLegacy}
		/>
	) : (
		<ChevronRightIcon
			label={formatMessage(messages.open)}
			color={token('color.text.subtle')}
			LEGACY_primaryColor={token('color.text.subtle')}
			LEGACY_fallbackIcon={ChevronRightIconLegacy}
		/>
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		onClick();
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'listRowGroupExpandIcon', {
			direction: expanded ? 'collapse' : 'expand',
			totalGroupItems,
		});
	}, [createAnalyticsEvent, expanded, onClick, totalGroupItems]);

	return (
		<Tooltip position="top" content={formatMessage(expanded ? messages.collapse : messages.expand)}>
			<ExpandIconWrapper
				onClick={handleClick}
				iconBefore={Icon}
				appearance="subtle"
				testId={`business-list.ui.list-view.group-item.expand-icon.expand-icon-wrapper-${rowIndex}`}
			/>
		</Tooltip>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ExpandIconWrapper = styled(Button)({
	transform: WRAPPER_TRANSFORM,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		position: 'absolute',
		width: '24px',
		height: '24px',
	},
});
